import { useMemo, useState } from 'react';
import type { SavedAction } from '@/types';
import { PinIcon } from '@/components/icons';
import { renderWithMentions } from '@/utils/renderMentions';

interface SlashCommandDropdownProps {
  actions: SavedAction[];
  query: string;
  onSelect: (template: string) => void;
  onClose: () => void;
}

export function SlashCommandDropdown({
  actions,
  query,
  onSelect,
  onClose,
}: SlashCommandDropdownProps) {
  const [search, setSearch] = useState(query);

  const filtered = useMemo(() => {
    const q = (search || query).trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((a) => a.template.toLowerCase().includes(q));
  }, [actions, search, query]);

  return (
    <div className="slash-command" role="listbox" aria-label="Saved actions">
      <p className="slash-command__hint">Saved actions — loads into the box; click the arrow to send</p>
      {actions.length === 0 ? (
        <p className="slash-command__empty">No saved actions yet. Pin prompts from the workspace.</p>
      ) : (
        <>
          <div className="slash-command__search">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <circle cx="5" cy="5" r="3.5" stroke="currentColor" strokeWidth="1.3" />
              <path d="M8 8l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <input
              className="slash-command__search-input"
              placeholder="Filter saved actions"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="slash-command__list">
            {filtered.length === 0 ? (
              <li className="slash-command__empty">No matches</li>
            ) : (
              filtered.map((action) => (
                <li key={action.id}>
                  <button
                    type="button"
                    className="slash-command__item"
                    role="option"
                    aria-selected={false}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onSelect(action.template)}
                  >
                    {action.isPinned ? (
                      <span className="slash-command__pin" aria-label="Pinned">
                        <PinIcon filled />
                      </span>
                    ) : null}
                    <span className="slash-command__text">{renderWithMentions(action.template)}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      )}
      <button type="button" className="slash-command__close" onClick={onClose} aria-label="Close">
        ×
      </button>
    </div>
  );
}

